import React from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Star, Mail, MapPin } from "lucide-react";

/**
 * ITER227: editor de varios correos y varias direcciones de entrega del cliente.
 * `value` = { correos: [{email, etiqueta, principal}], direcciones_entrega: [...] }
 */
export function ClienteContactosEditor({ correos = [], direcciones = [], onChange }) {
  const setCorreos = (next) => onChange({ correos: next, direcciones_entrega: direcciones });
  const setDirs = (next) => onChange({ correos, direcciones_entrega: next });

  const addCorreo = () => setCorreos([...correos, { email: "", etiqueta: "", principal: correos.length === 0 }]);
  const upCorreo = (i, k, v) => setCorreos(correos.map((c, j) => (j === i ? { ...c, [k]: v } : c)));
  const delCorreo = (i) => {
    const next = correos.filter((_, j) => j !== i);
    if (next.length && !next.some(c => c.principal)) next[0].principal = true;
    setCorreos(next);
  };
  const principalCorreo = (i) => setCorreos(correos.map((c, j) => ({ ...c, principal: j === i })));

  const addDir = () => setDirs([...direcciones, { etiqueta: "", direccion: "", ciudad: "", referencia: "", contacto: "", telefono: "", principal: direcciones.length === 0 }]);
  const upDir = (i, k, v) => setDirs(direcciones.map((d, j) => (j === i ? { ...d, [k]: v } : d)));
  const delDir = (i) => {
    const next = direcciones.filter((_, j) => j !== i);
    if (next.length && !next.some(d => d.principal)) next[0].principal = true;
    setDirs(next);
  };
  const principalDir = (i) => setDirs(direcciones.map((d, j) => ({ ...d, principal: j === i })));

  return (
    <div className="col-span-2 space-y-4" data-testid="cliente-contactos-editor">
      {/* CORREOS */}
      <div className="rounded-md border border-[#d1dbd6] overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 bg-[#f7f9f8] border-b border-[#d1dbd6]">
          <div>
            <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368]">
              <Mail size={13} className="inline mr-1" />Correos electrónicos
            </div>
            <div className="text-[11px] text-[#5c7368]">
              La factura se envía a todos (bodega, contabilidad…). El principal se usa en el SRI y en el estado de cuenta.
            </div>
          </div>
          <Button type="button" size="sm" variant="outline" onClick={addCorreo} data-testid="add-correo-btn">
            <Plus size={14} className="mr-1" />Agregar correo
          </Button>
        </div>
        <div className="p-3 space-y-2">
          {correos.length === 0 && (
            <div className="text-xs text-[#8ba397]">Sin correos. Agregue al menos uno para poder enviar la factura.</div>
          )}
          {correos.map((c, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`correo-row-${i}`}>
              <Input className="col-span-4 h-9 text-xs" type="email" value={c.email || ""}
                placeholder="correo@empresa.com" onChange={(e) => upCorreo(i, "email", e.target.value)}
                data-testid={`correo-email-${i}`} />
              <Select value={c.etiqueta || "Principal"} onValueChange={(v) => upCorreo(i, "etiqueta", v)}>
                <SelectTrigger className="col-span-4 h-9 text-xs" data-testid={`correo-etiqueta-${i}`}>
                  <SelectValue placeholder="Etiqueta" />
                </SelectTrigger>
                <SelectContent>
                  {["Principal", "Bodega", "Contabilidad", "Compras", "Gerencia", "Pagos", "Otro"].map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button type="button" size="sm" variant={c.principal ? "default" : "outline"}
                className={`col-span-3 h-9 text-xs ${c.principal ? "bg-[#205c45]" : ""}`}
                onClick={() => principalCorreo(i)} data-testid={`correo-principal-${i}`}>
                <Star size={13} className="mr-1" />{c.principal ? "Principal" : "Hacer principal"}
              </Button>
              <Button type="button" size="icon" variant="ghost" className="col-span-1 text-[#7a2514]"
                onClick={() => delCorreo(i)} data-testid={`correo-del-${i}`}>
                <Trash2 size={15} />
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* DIRECCIONES */}
      <div className="rounded-md border border-[#d1dbd6] overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 bg-[#f7f9f8] border-b border-[#d1dbd6]">
          <div>
            <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368]">
              <MapPin size={13} className="inline mr-1" />Direcciones de entrega
            </div>
            <div className="text-[11px] text-[#5c7368]">
              Se eligen al hacer la cotización, nota de pedido, factura o guía. La dirección fiscal se mantiene para el SRI.
            </div>
          </div>
          <Button type="button" size="sm" variant="outline" onClick={addDir} data-testid="add-direccion-btn">
            <Plus size={14} className="mr-1" />Agregar dirección
          </Button>
        </div>
        <div className="p-3 space-y-3">
          {direcciones.length === 0 && (
            <div className="text-xs text-[#8ba397]">Sin direcciones adicionales: se usará la dirección fiscal.</div>
          )}
          {direcciones.map((d, i) => (
            <div key={i} className="rounded border border-[#e2e8e5] p-2 space-y-2" data-testid={`direccion-row-${i}`}>
              <div className="grid grid-cols-12 gap-2">
                <Input className="col-span-3 h-9 text-xs" value={d.etiqueta || ""} placeholder="Etiqueta (Norte, Sur, Matriz…)"
                  onChange={(e) => upDir(i, "etiqueta", e.target.value)} data-testid={`dir-etiqueta-${i}`} />
                <Input className="col-span-6 h-9 text-xs" value={d.direccion || ""} placeholder="Dirección"
                  onChange={(e) => upDir(i, "direccion", e.target.value)} data-testid={`dir-direccion-${i}`} />
                <Button type="button" size="sm" variant={d.principal ? "default" : "outline"}
                  className={`col-span-2 h-9 text-xs ${d.principal ? "bg-[#205c45]" : ""}`}
                  onClick={() => principalDir(i)} data-testid={`dir-principal-${i}`}>
                  <Star size={13} className="mr-1" />{d.principal ? "Principal" : "Principal"}
                </Button>
                <Button type="button" size="icon" variant="ghost" className="col-span-1 text-[#7a2514]"
                  onClick={() => delDir(i)} data-testid={`dir-del-${i}`}>
                  <Trash2 size={15} />
                </Button>
              </div>
              <div className="grid grid-cols-12 gap-2">
                <Input className="col-span-3 h-9 text-xs" value={d.ciudad || ""} placeholder="Ciudad / sector"
                  onChange={(e) => upDir(i, "ciudad", e.target.value)} data-testid={`dir-ciudad-${i}`} />
                <Input className="col-span-3 h-9 text-xs" value={d.referencia || ""} placeholder="Referencia"
                  onChange={(e) => upDir(i, "referencia", e.target.value)} data-testid={`dir-referencia-${i}`} />
                <Input className="col-span-3 h-9 text-xs" value={d.contacto || ""} placeholder="Contacto que recibe"
                  onChange={(e) => upDir(i, "contacto", e.target.value)} data-testid={`dir-contacto-${i}`} />
                <Input className="col-span-3 h-9 text-xs" value={d.telefono || ""} placeholder="Teléfono"
                  onChange={(e) => upDir(i, "telefono", e.target.value)} data-testid={`dir-telefono-${i}`} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Selector de dirección de entrega para cotizaciones, NP, facturas y guías. */
export function DireccionEntregaSelect({ cliente, value, etiqueta, onChange, testId = "direccion-entrega" }) {
  const opciones = React.useMemo(() => {
    const dirs = (cliente?.direcciones_entrega || []).filter(d => d.direccion);
    if (cliente?.direccion_fiscal && !dirs.some(d => d.direccion === cliente.direccion_fiscal)) {
      dirs.unshift({ etiqueta: "Dirección fiscal", direccion: cliente.direccion_fiscal, principal: dirs.length === 0 });
    }
    return dirs;
  }, [cliente]);

  const seleccionada = opciones.find(d => d.direccion === value);

  if (!cliente) return null;

  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider text-[#5c7368]">Dirección de entrega</Label>
      <Select
        value={value || "__ninguna__"}
        onValueChange={(v) => {
          if (v === "__ninguna__") return onChange({ direccion: "", etiqueta: "" });
          const d = opciones.find(o => `${o.etiqueta}||${o.direccion}` === v);
          onChange({ direccion: d?.direccion || "", etiqueta: d?.etiqueta || "" });
        }}
      >
        <SelectTrigger className="h-10" data-testid={`${testId}-trigger`}>
          <SelectValue placeholder="Usar la dirección fiscal" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__ninguna__">Usar la dirección fiscal</SelectItem>
          {opciones.map((d, i) => (
            <SelectItem key={i} value={`${d.etiqueta}||${d.direccion}`} data-testid={`${testId}-opt-${i}`}>
              {d.etiqueta ? `${d.etiqueta} · ` : ""}{d.direccion}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {(seleccionada || etiqueta) && (
        <div className="text-[11px] text-[#5c7368] mt-1" data-testid={`${testId}-detalle`}>
          {seleccionada?.ciudad ? `${seleccionada.ciudad} · ` : ""}
          {seleccionada?.referencia ? `Ref: ${seleccionada.referencia} · ` : ""}
          {seleccionada?.contacto ? `Recibe: ${seleccionada.contacto}` : ""}
          {seleccionada?.telefono ? ` (${seleccionada.telefono})` : ""}
          {!seleccionada && etiqueta ? etiqueta : ""}
        </div>
      )}
    </div>
  );
}
